import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, PawPrint, ShieldCheck } from "lucide-react";
import { api, apiError, scrollToId, STATUS_STYLES, money } from "@/lib/helpers";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "dog", label: "Dogs" },
  { key: "cat", label: "Cats" },
];

function SponsorForm({ animal, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", amount: 30, message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Add your name and a valid email."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "sponsorship", amount: form.amount, monthly: true, animal_id: animal.id,
        name: form.name, email: form.email, message: form.message, origin_url: window.location.origin,
      });
      toast.success("Taking you to secure Stripe checkout…");
      setTimeout(() => { window.location.href = data.checkout_url; }, 700);
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`sponsor-form-${animal.name.toLowerCase()}`} className="mt-6 rounded-2xl bg-tinted p-5 border border-gold/30">
      <p className="font-serif text-lg text-pine">Sponsor {animal.name} monthly</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <input data-testid="sponsor-name-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
        <input data-testid="sponsor-email-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {[15, 30, 60].map((a) => (
          <button key={a} type="button" data-testid={`sponsor-amount-${a}`} onClick={() => setForm({ ...form, amount: a })}
            className={`rounded-full px-4 py-2 font-mono text-sm font-semibold transition-all ${form.amount === a ? "bg-gold text-charcoal" : "bg-white border border-stone-300 text-bark"}`}>
            ${a}/mo
          </button>
        ))}
      </div>
      <button data-testid="sponsor-submit-btn" disabled={busy} type="submit"
        className="mt-4 w-full rounded-full bg-pine py-3 font-bold text-sand hover:bg-pine-mid transition-all disabled:opacity-60">
        {busy ? "Sending…" : `Sponsor ${animal.name} for $${form.amount}/mo`}
      </button>
    </form>
  );
}

function AnimalModal({ animal, onClose, onAdopt }) {
  const [showSponsor, setShowSponsor] = useState(false);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-charcoal/70 backdrop-blur-sm p-0 sm:p-6" onClick={onClose}>
      <motion.div
        data-testid="animal-modal"
        initial={{ y: 60, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-sand shadow-2xl">
        <button data-testid="animal-modal-close" onClick={onClose} className="absolute right-4 top-4 z-10 rounded-full bg-charcoal/60 p-2 text-sand hover:bg-charcoal transition-colors">
          <X className="h-5 w-5" />
        </button>
        <div className="grid md:grid-cols-2">
          <div className="relative h-64 md:h-full min-h-[280px]">
            <img src={animal.image} alt={animal.name} className="absolute inset-0 h-full w-full object-cover" />
            <span className={`absolute left-4 top-4 rounded-full px-3 py-1.5 text-xs font-bold ${STATUS_STYLES[animal.status] || "bg-pine text-sand"}`}>
              {animal.status}
            </span>
          </div>
          <div className="p-6 sm:p-8">
            <p className="eyebrow">{animal.breed} · {animal.age} · {animal.sex}</p>
            <h3 className="mt-2 font-serif text-3xl text-charcoal" data-testid="animal-modal-name">{animal.name}</h3>
            <p className="mt-4 text-sm leading-relaxed text-bark" data-testid="animal-modal-story">{animal.story}</p>

            <div className="mt-5">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-gold-deep">Temperament</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {animal.temperament.map((t) => (
                  <span key={t} className="rounded-full bg-tinted px-3 py-1 text-xs font-medium text-pine">{t}</span>
                ))}
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-white border border-stone-200 p-4">
              <p className="flex items-center gap-2 text-xs font-bold text-pine"><ShieldCheck className="h-4 w-4" /> Medical</p>
              <p className="mt-1 text-xs text-bark">{animal.medical}</p>
              <p className="mt-2 flex items-center gap-2 text-xs font-bold text-pine"><PawPrint className="h-4 w-4" /> Good with</p>
              <p className="mt-1 text-xs text-bark">{animal.good_with.join(" · ")}</p>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button data-testid="animal-modal-adopt-cta" onClick={() => onAdopt(animal)}
                className="flex-1 rounded-full bg-gold py-3.5 font-bold text-charcoal hover:bg-gold-soft transition-all hover:scale-[1.02]">
                Apply to Adopt
              </button>
              <button data-testid="animal-modal-sponsor-cta" onClick={() => setShowSponsor(!showSponsor)}
                className="flex-1 rounded-full border-2 border-pine py-3.5 font-bold text-pine hover:bg-pine hover:text-sand transition-all">
                <Heart className="mr-1.5 inline h-4 w-4" /> Sponsor {money(30)}/mo
              </button>
            </div>
            <AnimatePresence>
              {showSponsor && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <SponsorForm animal={animal} onClose={onClose} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Animals({ onAdopt }) {
  const [filter, setFilter] = useState("all");
  const [animals, setAnimals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/animals").then(({ data }) => setAnimals(data)).catch(() => toast.error("Couldn't load the animals — refresh to retry.")).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const open = (e) => {
      const target = animals.find((a) => a.name === e.detail);
      if (target) setSelected(target);
    };
    window.addEventListener("wnt:open-animal", open);
    return () => window.removeEventListener("wnt:open-animal", open);
  }, [animals]);

  const shown = animals.filter((a) =>
    filter === "all" ? true : filter === "other" ? ["small", "farm"].includes(a.species) : a.species === filter
  );

  return (
    <section id="animals" className="relative bg-pine-deep py-20 sm:py-24 lg:py-28 grain overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-xl">
            <p className="dark-eyebrow">The residents</p>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl lg:text-4xl text-sand tracking-tight">
              Sanctuary and rescue residents.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f.key} data-testid={`animal-filter-${f.key}`} onClick={() => setFilter(f.key)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                  filter === f.key ? "bg-gold text-charcoal scale-105" : "bg-pine-mid/60 text-sand/80 hover:bg-pine-mid"
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && <p className="font-mono text-sm text-sand/50 col-span-full">Loading the crew…</p>}
          {shown.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.08}>
              <button data-testid={`animal-card-${a.name.toLowerCase()}`} onClick={() => setSelected(a)}
                className="group relative block w-full overflow-hidden rounded-3xl text-left aspect-[4/5] focus:outline-none focus:ring-2 focus:ring-gold">
                <img src={a.image} alt={`${a.name} — ${a.breed}`}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-pine-deep/95 via-pine-deep/20 to-transparent" />
                <span className={`absolute left-4 top-4 rounded-full px-3 py-1.5 text-[11px] font-bold ${STATUS_STYLES[a.status] || "bg-pine text-sand"}`}>
                  {a.status}
                </span>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="font-serif text-2xl text-sand">{a.name}</h3>
                  <p className="mt-0.5 font-mono text-xs text-sand/70">{a.breed} · {a.age}</p>
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-gold-soft opacity-0 -translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                    Read {a.name}'s story <PawPrint className="h-3.5 w-3.5" />
                  </p>
                </div>
              </button>
            </Reveal>
          ))}
          {!loading && !shown.length && (
            <p className="col-span-full font-mono text-sm text-sand/50">No animals in this filter yet.</p>
          )}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <AnimalModal animal={selected} onClose={() => setSelected(null)} onAdopt={(a) => { setSelected(null); onAdopt(a); }} />
        )}
      </AnimatePresence>
    </section>
  );
}
